import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Drawer from '@material-ui/core/Drawer';
import { CreationTaskDrawer } from 'features/daily-plan';
import { TASK_TYPES } from 'features/daily-plan/constants';
import useFetchWithStatus from 'hooks/fetchWithStatus';
import { useProfile } from 'hooks/profileActions';
import { useSearchProjectInProfiles } from 'hooks/searchProject';
import difference from 'lodash/difference';
import { useUserContext } from 'providers/UserProvider';
import queryString from 'query-string';
import { EntityRoutes } from 'routes/constants';
import { CompanyIncludes } from 'services/includes';
import strings from 'strings';
import { When } from 'UI/components/atoms/When';
import CompanyEdit from 'UI/components/organisms/CompanyEdit';
import CompanyForm from 'UI/components/organisms/CompanyForm';
import { FormFieldsMap, FormFieldsMapKeys } from 'UI/components/organisms/CompanyForm/fields';
import CompanySignStatusForm from 'UI/components/organisms/CompanySignStatusForm';
import {
  ClaimAccountabilityDrawer,
  useClaimAccountability
} from 'UI/components/organisms/inventoryProfiles/ClaimAccountability';
import { getSendoutActions } from 'UI/components/organisms/inventoryProfiles/SendoutsTab/sendoutsActions';
import { handleEntityEditionCompleted } from 'UI/components/organisms/inventoryProfiles/utils';
import ReassignRecruiterForm from 'UI/components/organisms/ReassignRecruiter';
import SendoutManager from 'UI/components/organisms/sendouts';
import InventoryProfileLayout from 'UI/components/templates/InventoryProfileLayout';
import { DateFormats } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import {
  CompanyEntity,
  EntityType,
  entityTypes,
  SearchProjectEntities,
  TabKeys
} from 'UI/constants/entityTypes';
import { PERSISTENCE_KEYS } from 'UI/constants/persistenceKeys';
import { Roles } from 'UI/constants/roles';
import { localTimeFormatter } from 'UI/utils';
import { updateUiState } from 'UI/utils/uiHelpers';

import { useCompanyProfileFeatureFlags } from './CompanyProfile.hooks';
import { CompanyPaymentStatusAlert } from './components';

const createTabsProps = ({
  companyId,
  endpoint,
  handleRefreshActivityStatus,
  handleSendoutClick,
  isLoading,
  itemName,
  results,
  searchProjectsProps,
  triggerTabRefresh,
  uiState
}) => [
  {
    id: TabKeys.JobOrders,
    tabProps: {
      profileId: companyId,
      isLoading,
      itemName,
      mode: EntityType.Company
    }
  },
  {
    id: TabKeys.Sendouts,
    tabProps: {
      profile: results,
      profileModule: EntityType.Company,
      onSendoutClick: handleSendoutClick,
      shouldRefresh: uiState.shouldRefresh
    }
  },
  {
    id: TabKeys.Placements,
    tabProps: {
      profileId: companyId,
      profileModule: EntityType.Company,
      onTriggerTabRefresh: triggerTabRefresh,
      isLoading
    }
  },
  {
    id: TabKeys.FeeAgreements,
    tabProps: {
      search: false,
      profile: results,
      companyId,
      profileModule: EntityType.Company,
      endpoint
    }
  },
  {
    id: TabKeys.HiringAuthorities,
    tabProps: {
      profile: results,
      isCompanyProfile: true
    }
  },
  {
    id: TabKeys.AssociatedCompanies,
    tabProps: {
      profileId: companyId,
      itemName,
      accountableId: results?.recruiter?.id
    }
  },
  searchProjectsProps,
  {
    id: TabKeys.Employees,
    tabProps: {
      profile: results
    }
  },
  {
    id: TabKeys.ActivityLogs,
    tabProps: {
      itemName,
      onActivityComplete: handleRefreshActivityStatus,
      profileId: companyId,
      profileModule: EntityType.Company,
      endpoint
    }
  },
  {
    id: TabKeys.Notes,
    tabProps: {
      onActivityComplete: handleRefreshActivityStatus,
      type: 'notes',
      profileId: companyId,
      itemName,
      search: false,
      endpoint,
      profileModule: EntityType.Company
    }
  },
  {
    id: TabKeys.Tasks,
    tabProps: {
      itemName,
      profileId: companyId
    }
  },
  {
    id: TabKeys.Sales,
    tabProps: {
      profileId: companyId
    }
  }
];

const DEFAULT_FIELDS_WHEN_EDITING_IMPORTED_COMPANIES = [
  FormFieldsMap.Specialty.key,
  FormFieldsMap.Specialties.key,
  FormFieldsMap.Subspecialty.key,
  FormFieldsMap.Subspecialties.key,
  FormFieldsMap.State.key,
  FormFieldsMap.City.key,
  FormFieldsMap.Zip.key,
  FormFieldsMap.Address.key,
  FormFieldsMap.Phone.key,
  FormFieldsMap.Ext.key,
  FormFieldsMap.Phones.key,
  FormFieldsMap.Website.key,
  FormFieldsMap.LinkedinUrl.key,
  FormFieldsMap.Email.key
];

/**
 * @param {Object} props
 * @param {boolean} props.enableUrlNavigation
 * @param {Object} props.layoutClassName
 * @param {*} props.match
 * @param {number} props.profileId
 */
const FPCompanyProfile = ({
  match,
  profileId,
  layoutClassName,
  enableUrlNavigation,
  onEditionCompleted
}) => {
  const [currentUser] = useUserContext();
  const companyId = profileId ?? match.params.id;

  const location = useLocation();

  const endpoint = `${Endpoints.Companies}/${companyId}`;
  const entityType = entityTypes.find(each => each.id === EntityType.Company);
  const history = useHistory();

  const { canUseDailyPlanTask, isAddDailyPlanTaskEnabled, isCurrentUserFromDataCoordination } =
    useCompanyProfileFeatureFlags();

  const [tabKeys, setTabKeys] = useState({
    [TabKeys.SearchProjects]: 0
  });

  const {
    handleEditClick,
    handleEditClosed,
    handleEditCompleted,
    handleProfileStateUpdate,
    handleReassignClick,
    handleReassignClosed,
    handleReassignCompleted,
    isLoading,
    refreshData,
    results,
    setUiState,
    state,
    status,
    uiState
  } = useProfile({
    entity: EntityType.Company,
    profileId: companyId,
    includes: CompanyIncludes,
    shouldLoadTitle: enableUrlNavigation
  });

  const isAlreadyAccountable = results?.recruiter?.id === currentUser.id;

  const {
    claimAccountabilityMenuItem,
    handleCloseClaimAccountabilityDrawer,
    handleSuccessClaimAccountability,
    openClaimAccountabilityDrawer
  } = useClaimAccountability({
    entity: CompanyEntity.id,
    isAlreadyAccountable
  });

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   R O L E S
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const recruiter = results?.recruiter ?? {};
  const {
    state: { results: recruiterRoles }
  } = useFetchWithStatus(recruiter?.id ? Endpoints.UserRoles.replace(':id', recruiter.id) : '');

  const isAssignedUserFromDataCoordination = (recruiterRoles || []).some(
    ({ id }) => id === Roles.DataCoordinator
  );

  const shouldEditBasicData =
    !isCurrentUserFromDataCoordination && isAssignedUserFromDataCoordination;

  const disabledEditKeys = shouldEditBasicData
    ? difference(FormFieldsMapKeys, DEFAULT_FIELDS_WHEN_EDITING_IMPORTED_COMPANIES)
    : [];

  const itemName = results?.name ?? '';
  const lastActivity = results?.last_activity_date;

  const handleRefreshActivityStatus = data =>
    handleProfileStateUpdate({ last_activity_date: data?.updated_at });
  const formattedDate = localTimeFormatter(lastActivity, DateFormats.SimpleDateTime);

  const triggerTabRefresh = nameTab =>
    setTabKeys(prevState => ({ ...prevState, [nameTab]: new Date().getTime() }));

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   S E N D O U T S
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const {
    handleSendoutClick,
    handleSendoutClosed,
    handleSendoutCompleted,
    handleSendoutNavigation
  } = getSendoutActions({ setUiState, history, triggerTabRefresh });

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   S E A R C H   P R O J E C T S   S E T T I N G S
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const { SearchProjectForms, SearchProjectsMenuItems, searchProjectTabPropsDefinition } =
    useSearchProjectInProfiles({
      entity: SearchProjectEntities.Company,
      profile: {
        id: results?.id,
        name: itemName
      },
      tabKey: tabKeys.searchProjects,
      triggerTabRefresh,
      endpoint,
      shouldRedirectToSearchProjectPreview: enableUrlNavigation
    });

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   C O M P A N Y   S T A T U S
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const [verifyType, setVerifyType] = useState({
    followUpId: null,
    opsVerification: null
  });

  const reassureCompany = results?.companyTypeReassure;
  const dialogProps = reassureCompany?.shouldAskRecruiter
    ? {}
    : {
        showCloseButton: true,
        mustComplete: false,
        isOpened: uiState.companyTypeOpen,
        onClose: () => updateUiState({ companyTypeOpen: false }, setUiState)
      };

  const handleProfileUpdate = updatedProfile => {
    if (updatedProfile?.files?.length > 0)
      handleProfileStateUpdate({ files: [...updatedProfile.files] });
  };

  useEffect(() => {
    const { followupid, showopsverificationmodal: showModal } = queryString.parse(location?.search);
    if ((verifyType.opsVerification && verifyType.followUpId) || !followupid || !showModal) return;
    setVerifyType({
      followUpId: followupid,
      opsVerification: showModal
    });
    updateUiState({ opsVerificationOpen: showModal === 'true' }, setUiState);
  }, [location.search, verifyType, setUiState]);

  const handleVerifyClose = useCallback(() => {
    history.replace(location.pathname);
    setVerifyType({ followUpId: null, opsVerification: null });
  }, [history, location.pathname]);

  const tabsProps = createTabsProps({
    companyId,
    endpoint,
    handleRefreshActivityStatus,
    handleSendoutClick,
    isLoading,
    itemName,
    results,
    triggerTabRefresh,
    uiState,
    searchProjectsProps: searchProjectTabPropsDefinition
  });

  const headerProps = {
    sectionName: 'Company',
    title: itemName
  };

  const customMenuItems = [
    ...SearchProjectsMenuItems,
    {
      title: 'Update Company Type',
      action: () => updateUiState({ companyTypeOpen: true }, setUiState),
      visible: reassureCompany?.recruiterCanRequest
    },
    {
      title: 'Add Action',
      action: () => setUiState(prev => ({ ...prev, isDailyPlanTaskDrawerOpen: true })),
      visible: isAddDailyPlanTaskEnabled && canUseDailyPlanTask
    },
    claimAccountabilityMenuItem
  ];

  const lastPayment = localTimeFormatter(results?.last_payment, DateFormats.SimpleDateTime);
  const summaryLabels = [{ title: 'Last Payment', content: lastPayment }];

  const handleTaskDrawerClose = () => {
    setUiState(prev => ({ ...prev, isDailyPlanTaskDrawerOpen: false }));
  };

  const handleOnTaskCreated = () => {
    handleTaskDrawerClose();
    history.push(EntityRoutes.DailyPlan);
  };

  return (
    <>
      <InventoryProfileLayout
        activityLabel={formattedDate}
        addItemLabel="Add a Collaborator"
        contentPageLayoutProps={{ className: layoutClassName }}
        enableUrlNavigation={enableUrlNavigation}
        entityType={entityType}
        headerAlert={<CompanyPaymentStatusAlert companyId={companyId} isLoading={isLoading} />}
        headerCardProps={headerProps}
        inventoryType={results?.type}
        profileMenuProps={{
          onReassignClick: handleReassignClick,
          onEditClick: handleEditClick,
          customMenuItems,
          isEditionCheckIgnored: shouldEditBasicData
        }}
        ProfileTabsProps={{
          endpoint,
          organizationStorageKey: PERSISTENCE_KEYS.PROFILE_TABS.COMPANIES,
          organizationSubheader: `${CompanyEntity.singular} ${strings.inventoryProfiles.title}`,
          profileTabProps: tabsProps
        }}
        ProfileUiStateManagerProps={{
          status,
          responseStatusCode: state.responseStatusCode,
          entityRoute: EntityRoutes.Companies,
          onRefreshProfileClick: refreshData,
          entity: CompanyEntity
        }}
        results={results}
        summaryLabels={summaryLabels}
      />
      <When condition={!isLoading}>
        <>
          {(reassureCompany?.shouldAskRecruiter || uiState.companyTypeOpen) && (
            <CompanySignStatusForm
              type="dialog"
              loading={uiState.isLoading}
              companyId={companyId}
              company={results}
              dialogProps={dialogProps}
              reassure={reassureCompany?.inProgress}
              onUpdateCompany={handleProfileUpdate}
            />
          )}
          {verifyType.followUpId && verifyType.opsVerification && (
            <CompanySignStatusForm
              type="drawer"
              loading={uiState.isLoading}
              company={results}
              companyId={companyId}
              onUpdateCompany={handleProfileUpdate}
              followUpId={verifyType.followUpId}
              onCloseDrawer={handleVerifyClose}
            />
          )}

          {uiState.isSendoutOpen && uiState.selectedSendoutId && (
            <Drawer open onClose={handleSendoutClosed}>
              <SendoutManager
                id={uiState.selectedSendoutId}
                onEdit={handleSendoutCompleted}
                onDelete={handleSendoutCompleted}
                onClose={handleSendoutClosed}
                onNavigate={handleSendoutNavigation}
              />
            </Drawer>
          )}

          {uiState.isEditOpen && results && (
            <CompanyEdit
              company={results}
              onEditCompleted={handleEntityEditionCompleted({
                handleEditCompleted,
                enableUrlNavigation,
                onEditionCompleted
              })}
              onEditClosed={handleEditClosed}
              CompanyForm={<CompanyForm disabledEditKeys={disabledEditKeys} />}
            />
          )}
          <When condition={uiState.isReassignOpen && !!results?.id}>
            <ReassignRecruiterForm
              baseEndpoint={Endpoints.Companies}
              item={results}
              entityType={entityType}
              onReassignCompleted={handleReassignCompleted}
              onReassignClosed={handleReassignClosed}
            />
          </When>
          <When condition={uiState.isDailyPlanTaskDrawerOpen}>
            <CreationTaskDrawer
              allowedTaskTypes={[TASK_TYPES.marketCandidate, TASK_TYPES.other]}
              onClose={handleTaskDrawerClose}
              entityData={{
                id: results.id,
                name: results?.name
              }}
              entityType={EntityType.Company}
              onTaskCreated={handleOnTaskCreated}
            />
          </When>
          <SearchProjectForms />
        </>
      </When>

      <When condition={openClaimAccountabilityDrawer}>
        <ClaimAccountabilityDrawer
          entityId={companyId}
          entityType={CompanyEntity.id}
          onClose={handleCloseClaimAccountabilityDrawer}
          onSuccess={handleSuccessClaimAccountability}
          open={openClaimAccountabilityDrawer}
        />
      </When>
    </>
  );
};

FPCompanyProfile.defaultProps = {
  enableUrlNavigation: true
};

export default FPCompanyProfile;
